/**
 * @file inward-sla-bell.tsx
 * @module app/modules/inward/components/inward-sla-bell
 * @description Red swinging bell fixed on the right side of every admin page while any of the
 *   user's pending actions (selected warehouse) is past its SLA. Plays a ding every N minutes while
 *   something is overdue (N = "Overdue reminder sound" in Inward SLA settings, default 60), and
 *   also when another action becomes overdue (at most once a minute). A page reload does not ding
 *   again before the interval. The settings on/off switch applies to super admins only (they see
 *   every role's actions); other users always hear it for the actions their access allows. Clicking it opens the dashboard at Pending actions.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import { canPerformInwardAction, InwardAction } from "../utils/inward-access";
import { dingDue, lastDingAt, playDing, rememberDing, unlockAudio } from "../utils/inward-ding";
import { dueText, PendingActionsResponse, REMINDER_DEFAULT_MINUTES } from "../utils/inward-sla";
import "../styles/inward.css";

const REFRESH_MS = 60_000;
const TICK_MS = 30_000;

/** Dashboard section the bell points to (admin-dashboard.tsx). */
export const PENDING_ACTIONS_ANCHOR = "pending-actions";

const InwardSlaBell = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const canView = canPerformInwardAction(currentUser, InwardAction.View);
  const warehouseId = selectedWarehouse?._id;
  const [data, setData] = useState<PendingActionsResponse | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // overdue count already seen in this session (null until the first load)
  const seenOverdue = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!warehouseId) return;
    const response = await new APICallService(INWARD.pendingActions, {}, "", "", "", "", PermissionModule.Inward, warehouseId).callAPI();
    if (response?.items) setData(response);
  }, [warehouseId]);

  useEffect(() => {
    if (!canView || !warehouseId) {
      setData(null);
      return undefined;
    }
    load();
    const refresh = window.setInterval(load, REFRESH_MS);
    const tick = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      window.clearInterval(refresh);
      window.clearInterval(tick);
    };
  }, [canView, warehouseId, load]);

  // sound may only start after the user has interacted with the page
  useEffect(() => {
    document.addEventListener("pointerdown", unlockAudio, true);
    document.addEventListener("keydown", unlockAudio, true);
    return () => {
      document.removeEventListener("pointerdown", unlockAudio, true);
      document.removeEventListener("keydown", unlockAudio, true);
    };
  }, []);

  const overdue = (data?.items ?? []).filter((i) => dueText(i, new Date(now)).overdue).length;
  const interval = data?.reminderIntervalMinutes || REMINDER_DEFAULT_MINUTES;
  // the on/off switch is for super admins; everyone else always hears their own overdue actions
  const soundOn = !data?.superAdmin || data?.reminderSoundEnabled !== false;

  useEffect(() => {
    if (!data) return;
    const increased = seenOverdue.current !== null && overdue > seenOverdue.current;
    seenOverdue.current = overdue;
    if (!overdue || !soundOn) return;
    const last = lastDingAt();
    if (dingDue(last, now, interval) || (increased && dingDue(last, now, 1))) {
      if (playDing()) rememberDing(now);
    }
  }, [data, overdue, now, interval, soundOn]);

  if (!overdue) return null;

  return (
    <button
      type="button"
      className="inward-sla-bell"
      onClick={() => navigate("/dashboard", { state: { focus: PENDING_ACTIONS_ANCHOR } })}
      aria-label={`${overdue} pending action${overdue === 1 ? "" : "s"} past SLA: open Pending actions`}
      title={`${overdue} pending action${overdue === 1 ? "" : "s"} past SLA`}
      data-testid="inward-sla-bell"
    >
      <svg className="inward-sla-bell__icon" viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"
        />
      </svg>
      <span className="inward-sla-bell__count" data-testid="inward-sla-bell-count">
        {overdue}
      </span>
    </button>
  );
};

export default InwardSlaBell;
